// hooks/usePdars.ts
import { useState, useEffect } from "react";
import { MapEngine } from "@/client/map/core/MapEngine";
import type { Pdars } from "@/client/map/types";

export function usePdarSelection() {
  // Just the selected pdar_id(s) — the full Pdars objects are always
  // available via `pdars`, so keeping a second copy of them here would be
  // redundant. Look up `pdars.find(p => p.pdar_id === id)` when you need the
  // full entity.
  const [selectedPdarIds, setSelectedPdarIds] = useState<string[]>([]);
  // Array (not Map) so this is directly mappable in JSX (`pdars.map(...)`)
  // and JSON.stringify-friendly for debugging.
  const [pdars, setPdars] = useState<Pdars[]>([]);
  // pdar_id -> resolved orientation in degrees (wire orientation + the
  // client-side calibration offset), kept live from PdarsManager so a
  // component can render the current value (e.g. to initialize a slider)
  // without reaching into the manager directly.
  const [orientationByPdarId, setOrientationByPdarId] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    let interval: any;
    let cleanupBusListener: (() => void) | null = null;

    // Split into two syncs so a selection change (click, or select/deselect/
    // toggle) doesn't also force-rebuild the full `pdars` array, and a
    // routine data batch doesn't force a `selectedPdarIds` update — each
    // event only touches the state it's actually about, so a click updates
    // instantly instead of waiting behind (or being bundled uselessly with)
    // the ~10x/sec data-batch churn.
    const syncSelectionIds = () => {
      const manager = MapEngine.getInstance()?.getPdarsManager();
      if (manager) {
        setSelectedPdarIds((prev) => {
          const next = manager.getSelection().getSelected() ?? [];
          // Bail out of the setState if nothing actually changed, so React
          // doesn't re-render on every duplicate notify.
          if (
            prev.length === next.length &&
            prev.every((id, i) => id === next[i])
          ) {
            return prev;
          }
          return next;
        });
      }
    };

    const syncPdars = () => {
      const manager = MapEngine.getInstance()?.getPdarsManager();
      if (manager) {
        setPdars(Array.from(manager.getAllPdars().values()));
      }
    };

    // Only touches the single sensor whose orientation just changed, so
    // dragging the calibration slider doesn't force a rebuild of `pdars` or
    // `selectedPdarIds` on every tick.
    const syncOrientation = ({
      pdar_id,
      orientation_deg,
    }: {
      pdar_id: string;
      orientation_deg: number;
    }) => {
      setOrientationByPdarId((prev) => {
        if (prev[pdar_id] === orientation_deg) return prev;
        return { ...prev, [pdar_id]: orientation_deg };
      });
    };

    const attachSelectionListener = () => {
      const manager = MapEngine.getInstance()?.getPdarsManager();
      const eventBus = MapEngine.getInstance()?.getEventBus();

      if (manager && eventBus) {
        clearInterval(interval);

        // Run an immediate sync frame
        syncSelectionIds();
        syncPdars();

        // Listen for internal explicit state triggers
        eventBus.on("pdar:selected-pdar-updated", syncSelectionIds);
        // Also listen to raw batch data stream updates to keep values updated in real time!
        eventBus.on("data:pdar-batch-updated", syncPdars);
        eventBus.on("pdar:orientation-updated", syncOrientation);

        cleanupBusListener = () => {
          eventBus.off("pdar:selected-pdar-updated", syncSelectionIds);
          eventBus.off("data:pdar-batch-updated", syncPdars);
          eventBus.off("pdar:orientation-updated", syncOrientation);
        };
      }
    };

    // Keep scanning until the engine manager mounts
    interval = setInterval(attachSelectionListener, 100);
    attachSelectionListener();

    return () => {
      clearInterval(interval);
      if (cleanupBusListener) cleanupBusListener();
    };
  }, []);

  const select = (pdar_id: string) => {
    MapEngine.getInstance()?.getPdarsManager()?.selectPdar(pdar_id);
  };

  const deselect = (pdar_id: string) => {
    MapEngine.getInstance()?.getPdarsManager()?.deselectPdar(pdar_id);
  };

  const toggle = (pdar_id: string) => {
    MapEngine.getInstance()
      ?.getPdarsManager()
      ?.getSelection()
      .toggleAircraft(pdar_id);
  };

  const isSelected = (pdar_id: string): boolean => {
    return (
      MapEngine.getInstance()?.getPdarsManager()?.isSelected(pdar_id) ?? false
    );
  };

  // Sets a sensor's client-side orientation calibration offset (degrees,
  // any range — e.g. straight from a -180..180 slider). Rotates that
  // sensor's icon and AoA lines on the map; never sent to the server.
  const setOrientationOffset = (pdar_id: string, offsetDeg: number) => {
    MapEngine.getInstance()
      ?.getPdarsManager()
      ?.setOrientationOffset(pdar_id, offsetDeg);
  };

  const getOrientationOffset = (pdar_id: string): number => {
    return (
      MapEngine.getInstance()?.getPdarsManager()?.getOrientationOffset(pdar_id) ??
      0
    );
  };

  return {
    selectedPdarIds,
    setSelectedPdarIds,
    pdars,
    select,
    deselect,
    toggle,
    isSelected,
    orientationByPdarId,
    setOrientationOffset,
    getOrientationOffset,
  };
}
